import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { User, LogOut, Crown, ChevronDown } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { cn } from "@/lib/utils";

export default function UserMenu({ onUpgrade }) {
  const { user, profile, isPremium, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  if (!user) {
    return (
      <div className="space-y-1">
        <button onClick={() => navigate("/login")}
          className="w-full flex items-center gap-3 px-4 py-2.5 text-sm font-medium text-[#A1A1AA] hover:text-white hover:bg-[#18181B] rounded-lg transition-colors">
          <User className="w-4 h-4" /> Sign In
        </button>
        <button onClick={() => navigate("/signup")}
          className="w-full flex items-center gap-3 px-4 py-2.5 text-sm font-medium bg-[#22c55e]/10 text-[#22c55e] hover:bg-[#22c55e]/20 rounded-lg transition-colors">
          <Crown className="w-4 h-4" /> Get Premium
        </button>
      </div>
    );
  }

  return (
    <div ref={ref} className="relative">
      <button onClick={() => setOpen(!open)}
        className="w-full flex items-center gap-3 px-4 py-2.5 text-sm font-medium text-[#A1A1AA] hover:text-white hover:bg-[#18181B] rounded-lg transition-colors"
      >
        <div className={cn("w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold",
          isPremium ? "bg-[#22c55e] text-black" : "bg-[#27272A] text-white")}>
          {(user.displayName || user.email || "?")[0].toUpperCase()}
        </div>
        <div className="flex-1 text-left min-w-0">
          <p className="text-white text-xs font-medium truncate">{user.displayName || user.email}</p>
          <p className={cn("text-[10px]", isPremium ? "text-[#22c55e]" : "text-[#52525B]")}>
            {isPremium ? "⚡ Premium" : "Free plan"}
          </p>
        </div>
        <ChevronDown className={cn("w-3.5 h-3.5 transition-transform shrink-0", open && "rotate-180")} />
      </button>

      {open && (
        <div className="absolute bottom-full left-0 right-0 mb-1 bg-[#18181B] border border-[#27272A] rounded-lg shadow-xl overflow-hidden z-50">
          <div className="px-4 py-3 border-b border-[#27272A]">
            <p className="text-xs text-white font-medium">{user.displayName || "User"}</p>
            <p className="text-[10px] text-[#52525B] truncate">{user.email}</p>
          </div>
          {!isPremium && (
            <button onClick={() => { onUpgrade?.(); setOpen(false); }}
              className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-[#22c55e] hover:bg-[#22c55e]/10 transition-colors">
              <Crown className="w-4 h-4" /> Upgrade to Premium
            </button>
          )}
          <button onClick={async () => { await logout(); setOpen(false); navigate("/login"); }}
            className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-[#A1A1AA] hover:text-white hover:bg-[#27272A] transition-colors">
            <LogOut className="w-4 h-4" /> Sign Out
          </button>
        </div>
      )}
    </div>
  );
}
